/**
 * Browser side of the editor session (gateway `/auth/*`, see
 * apps/backend/src/auth.ts). The session itself is an HttpOnly cookie the page
 * cannot read; this only mirrors what the gateway says about it, so a reload
 * keeps the editor signed in.
 */
import type { Auth, AuthUser, SignInResult } from "./deps.js";

interface SessionBody {
	authenticated?: boolean;
	user?: AuthUser;
	roles?: string[];
}

/** Create the {@link Auth} for a gateway at `base` (same origin by default). */
export function createSessionAuth(base = "", fetchImpl: typeof fetch = fetch): Auth {
	let user: AuthUser | null = null;
	let roles: string[] = [];

	function apply(body: SessionBody): void {
		if (body.authenticated && body.user) {
			user = body.user;
			roles = Array.isArray(body.roles) ? body.roles : [];
		} else {
			user = null;
			roles = [];
		}
	}

	async function post(path: string, body?: unknown): Promise<Response> {
		return fetchImpl(`${base}${path}`, {
			method: "POST",
			credentials: "same-origin",
			headers: { "content-type": "application/json", accept: "application/json" },
			...(body === undefined ? {} : { body: JSON.stringify(body) }),
		});
	}

	return {
		async refresh() {
			const res = await fetchImpl(`${base}/auth/session`, {
				credentials: "same-origin",
				headers: { accept: "application/json" },
			});
			apply(res.ok ? ((await res.json()) as SessionBody) : {});
		},

		async signIn(email, password): Promise<SignInResult> {
			let res: Response;
			try {
				res = await post("/auth/login", { email, password });
			} catch {
				return "unavailable";
			}
			if (res.ok) {
				apply((await res.json()) as SessionBody);
				return "ok";
			}
			if (res.status === 400 || res.status === 401) return "invalid";
			if (res.status === 429) return "rate_limited";
			return "unavailable";
		},

		async signOut() {
			try {
				await post("/auth/logout");
			} finally {
				apply({});
			}
		},

		isAuthenticated: () => user !== null,
		getRoles: () => [...roles],
		getUser: () => (user ? { ...user } : null),
	};
}
